import React from "react";

import { useAuth } from "@wc/domain/authn";
import { useOrg } from "@wc/domain/organizations";
import { getUserTeamsWithPrimary } from "@wc/domain/team-membership";

/**
 * @typedef {object} Team
 * @property {string} id The team id
 * @property {string} name The team name
 * @property {boolean} isPrimary Whether this is the user's primary team
 */

/**
 * @typedef {object} UseUserTeamsResult
 * @property {Team[]} teams The user's teams
 * @property {string|null} primaryTeamId The primary team id
 * @property {boolean} hasMultipleTeams Whether user has more than one team
 * @property {boolean} loading Whether teams are being loaded
 */

/**
 * Hook to get the current user's teams in the current organization
 * @returns {UseUserTeamsResult} The user teams info
 */
export default function useUserTeams() {
  const authData = useAuth();
  const orgData = useOrg();
  const [teams, setTeams] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const loadTeams = async () => {
      if (!orgData?.org?.id || !authData.isLoggedIn()) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const userTeams = await getUserTeamsWithPrimary(
          authData.getCurrentUser().id,
          orgData.org.id,
        );
        setTeams(userTeams);
      } catch (error) {
        console.error("Failed to load user teams:", error);
        setTeams([]);
      } finally {
        setLoading(false);
      }
    };

    loadTeams();
  }, [authData, orgData?.org?.id]);

  const primaryTeam = teams.find((t) => t.isPrimary);
  const primaryTeamId =
    primaryTeam?.id || (teams.length > 0 ? teams[0].id : null);

  return {
    teams,
    primaryTeamId,
    hasMultipleTeams: teams.length > 1,
    loading,
  };
}
